"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _componentsBase = require("@telus-uds/components-base");
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _utils = require("../utils");
var _NavigationItem = _interopRequireDefault(require("./NavigationItem"));
var _NavigationSubMenu = _interopRequireDefault(require("./NavigationSubMenu"));
var _collapseItems = _interopRequireDefault(require("./collapseItems"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _componentsBase.selectSystemProps)([_utils.htmlAttrs]);
const Heading = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "NavigationBar__Heading",
  componentId: "components-web__sc-1vis1gt-0"
})({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  justifyContent: 'flex-start',
  '> *': {
    display: 'contents',
    letterSpacing: 0
  }
});

/**
 * NavigationBar can be used to allow customers to consistently navigate across
 * key pages within a specific product line
 */
const NavigationBar = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    accessibilityRole = 'navigation',
    heading,
    headingLevel = 'h1',
    items,
    onChange,
    selectedId,
    value,
    LinkRouter,
    linkRouterProps,
    tokens,
    ...rest
  } = _ref;
  const {
    currentValue,
    setValue
  } = (0, _componentsBase.useInputValue)({
    value,
    initialValue: selectedId,
    onChange
  });
  (0, _componentsBase.useHash)((hash, event) => {
    let hashItem = hash && items.find(_ref2 => {
      let {
        href
      } = _ref2;
      return hash === href;
    });
    if (!hashItem) {
      const parentItem = items.find(_ref3 => {
        let {
          items: parentItems
        } = _ref3;
        return parentItems?.some(_ref4 => {
          let {
            href
          } = _ref4;
          return hash === href;
        });
      });
      hashItem = parentItem?.items.find(_ref5 => {
        let {
          href
        } = _ref5;
        return hash === href;
      });
    }
    const hashId = hashItem && (hashItem.id || hashItem.label);
    if (hashId) setValue(hashId, event);
  }, [items, setValue]);
  const direction = (0, _componentsBase.useResponsiveProp)({
    xs: 'column',
    sm: 'row'
  });
  const itemsForViewport = (0, _componentsBase.useResponsiveProp)({
    xs: (0, _collapseItems.default)(items, currentValue),
    lg: items
  });
  const openOverlayRef = _react.default.useRef(null);
  const [openSubMenuId, setOpenSubMenuId] = _react.default.useState(null);
  const handleSubMenuClose = event => {
    if (event.type === 'keydown') {
      if (event.key === 'Escape' || event.key === 27) {
        setOpenSubMenuId(null);
      }
    } else if (event.type === 'click' && openOverlayRef?.current && event.target && !openOverlayRef?.current?.contains(event.target)) {
      setOpenSubMenuId(null);
    } else if (event.type === 'touchstart' && openOverlayRef?.current && event.touches[0].target && !openOverlayRef?.current?.contains(event.touches[0].target)) {
      setOpenSubMenuId(null);
    }
  };
  const navRefDefault = _react.default.useRef(null);
  const navRef = ref ?? navRefDefault;
  const itemsRef = _react.default.useRef(null);

  // Close the submenu when the user clicks outside the navigation bar
  const handleMouseDown = _react.default.useCallback(event => {
    if (navRef.current && itemsRef.current) {
      // Get the bounding rectangles of the navigation bar and the items container
      const navRect = navRef.current.getBoundingClientRect();
      const itemsRect = itemsRef.current.getBoundingClientRect();

      // Check if the click was outside the navigation bar and the items container
      const isOutsideNav = event.clientX < navRect.left || event.clientX > navRect.right || event.clientY < navRect.top || event.clientY > navRect.bottom;
      const isOutsideItems = event.clientX < itemsRect.left || event.clientX > itemsRect.right || event.clientY < itemsRect.top || event.clientY > itemsRect.bottom;
      if (isOutsideNav && isOutsideItems) {
        setOpenSubMenuId(null);
      }
    }
  }, [navRef, itemsRef, setOpenSubMenuId]);

  // TODO: create a custom hook for that and use here and in the Footnote
  _react.default.useEffect(() => {
    // Add listeners for mouse clicks outside and for key presses
    document.addEventListener('mousedown', handleMouseDown);
    if (openSubMenuId !== null) {
      window.addEventListener('click', handleSubMenuClose);
      window.addEventListener('keydown', handleSubMenuClose);
      window.addEventListener('touchstart', handleSubMenuClose);
    }
    return () => {
      // Remove listeners when the component is unmounted
      document.removeEventListener('mousedown', handleMouseDown);
      if (openSubMenuId !== null) {
        window.removeEventListener('click', handleSubMenuClose);
        window.removeEventListener('keydown', handleSubMenuClose);
        window.removeEventListener('touchstart', handleSubMenuClose);
      }
    };
  }, [openSubMenuId, handleMouseDown]);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_componentsBase.StackView, {
    accessibilityRole: accessibilityRole,
    direction: direction,
    ref: navRef,
    space: 2,
    tokens: {
      alignItems: direction === 'column' ? 'flex-start' : 'center',
      justifyContent: 'flex-end'
    },
    ...selectProps(rest),
    children: [heading && /*#__PURE__*/(0, _jsxRuntime.jsx)(Heading, {
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Typography, {
        variant: {
          size: 'h5'
        },
        heading: headingLevel,
        children: heading
      })
    }), itemsForViewport?.map((_ref6, index) => {
      let {
        href,
        label,
        id,
        onClick,
        ref: itemRef,
        LinkRouter: ItemLinkRouter = LinkRouter,
        linkRouterProps: itemLinkRouterProps,
        items: nestedItems,
        ...itemRest
      } = _ref6;
      const itemId = id ?? label;
      const handleClick = event => {
        if (nestedItems) {
          setOpenSubMenuId(openSubMenuId !== itemId ? itemId : null);
          return;
        }
        if (href?.startsWith('#')) {
          (0, _utils.scrollToAnchor)(href, event, () => setValue(itemId, event));
        } else {
          setValue(itemId, event);
        }
        onClick?.(event);
      };
      const ItemComponent = nestedItems ? _NavigationSubMenu.default : _NavigationItem.default;
      const isOpen = itemId === openSubMenuId;
      const scrollableNestedItems = nestedItems?.map(item => ({
        ...item,
        onPress: event => {
          const nestedItemId = item.id ?? item.label;
          (0, _utils.scrollToAnchor)(item.href, event, () => setValue(nestedItemId, event));
        }
      })) ?? nestedItems;
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(ItemComponent, {
        ref: itemRef,
        href: href,
        onClick: handleClick
        // TODO: refactor to pass selected ID via context
        ,
        selectedId: currentValue,
        index: index,
        LinkRouter: ItemLinkRouter,
        linkRouterProps: {
          ...linkRouterProps,
          ...itemLinkRouterProps
        },
        items: scrollableNestedItems,
        tokens: tokens,
        selected: itemId === currentValue,
        itemsContainerRef: itemsRef,
        ...itemRest,
        ...(scrollableNestedItems && {
          isOpen
        }),
        ...(scrollableNestedItems && isOpen && {
          openOverlayRef
        }),
        children: label
      }, itemId);
    })]
  });
});
NavigationBar.displayName = 'NavigationBar';
NavigationBar.propTypes = {
  ...selectedSystemPropTypes,
  ..._componentsBase.withLinkRouter.propTypes,
  /**
   * NavigationBar pages
   *
   * Each `item` object must contain:
   *  - `heading` - user-facing text in the tab link
   *  - `href` - the URL of the page linked to.
   *  - `id` - a stable, unique identifier of the page within the set. Not written into the HTML.
   */
  items: _propTypes.default.arrayOf(_propTypes.default.shape({
    label: _propTypes.default.string.isRequired,
    href: _propTypes.default.string,
    id: _propTypes.default.string.isRequired,
    onClick: _propTypes.default.func,
    selected: _propTypes.default.bool,
    LinkRouter: _componentsBase.withLinkRouter.propTypes?.LinkRouter,
    linkRouterProps: _componentsBase.withLinkRouter.propTypes?.linkRouterProps,
    // One layer of nested links is allowed
    items: _propTypes.default.arrayOf(_propTypes.default.shape({
      label: _propTypes.default.string.isRequired,
      href: _propTypes.default.string,
      id: _propTypes.default.string.isRequired,
      onClick: _propTypes.default.func,
      selected: _propTypes.default.bool,
      LinkRouter: _componentsBase.withLinkRouter.propTypes?.LinkRouter,
      linkRouterProps: _componentsBase.withLinkRouter.propTypes?.linkRouterProps
    }))
  })).isRequired,
  /**
   * Common navigation bar heading.
   */
  heading: _propTypes.default.string,
  /**
   * Sets the `headingLevel` of the `heading`
   */
  headingLevel: _propTypes.default.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  /**
   * Initial selected item ID
   */
  selectedId: _propTypes.default.string,
  /**
   * Optional function to be called on pressing a link
   */
  onChange: _propTypes.default.func,
  /**
   * Controlled value for selected item ID
   */
  value: _propTypes.default.string,
  /**
   * Accesibility role for stackview
   */
  accessibilityRole: _propTypes.default.string
};
var _default = exports.default = NavigationBar;